/**
 * mutationErrorHandler — canonical onError callback factory for React Query mutations.
 *
 * Usage:
 *   import { createMutationErrorHandler } from '@/lib/mutationErrorHandler';
 *   // inside a hook:
 *   const { toast } = useToast();
 *   return useMutation({ ..., onError: createMutationErrorHandler(toast) });
 *
 * Behaviour:
 *  - 419 CSRF token mismatch → human-readable "refresh the page" message
 *  - 422 Validation error → first field message shown; field-level errors are
 *    NOT set here (callers must handle that in the component via RHF setError)
 *  - All other 4xx/5xx → shows server-provided message or fallback
 *  - Non-Axios errors (network, code) → shows generic fallback
 *
 * The toast title is always "Error" from the common namespace to avoid
 * duplicating i18n wiring in every hook file. Callers may override by passing
 * a custom `title`.
 */
import axios from 'axios';

export interface ToastFn {
  (opts: {
    title?: string;
    description?: string;
    variant?: 'default' | 'destructive';
  }): void;
}

export interface MutationErrorHandlerOptions {
  /**
   * When true, suppresses the toast for 422 validation errors.
   * Use this when the calling component maps 422 field errors to RHF setError
   * inline — showing both a toast AND inline errors would duplicate the message.
   */
  suppress422Toast?: boolean;
}

/**
 * Returns an `onError` callback suitable for `useMutation({ onError: ... })`.
 *
 * @param toast   - The `toast` function from `useToast()`
 * @param title   - Optional override for the toast title (defaults to "Error")
 * @param options - Optional behaviour overrides (e.g. suppress422Toast)
 */
export function createMutationErrorHandler(
  toast: ToastFn,
  title = 'Error',
  options: MutationErrorHandlerOptions = {},
): (error: Error) => void {
  return (error: Error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data as
        | { message?: string; errors?: Record<string, string[]> }
        | undefined;

      if (status === 419) {
        toast({
          title,
          description:
            'Your session token expired. Please refresh the page and try again.',
          variant: 'destructive',
        });
        return;
      }

      if (status === 422 && data?.errors) {
        // When the caller maps field errors to RHF setError, suppress the toast
        // so the user doesn't see both inline highlights AND a duplicate toast.
        if (options.suppress422Toast) return;

        // Otherwise show the first validation message; inline field errors must
        // be surfaced by the calling component via RHF setError.
        const firstError = Object.values(data.errors)[0]?.[0];
        toast({
          title,
          description: firstError ?? data.message ?? 'Please check the form for errors.',
          variant: 'destructive',
        });
        return;
      }

      const message = data?.message;
      toast({
        title,
        description: message ?? 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } else {
      toast({
        title,
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };
}

/**
 * Extract a human-readable message from a thrown mutation error, for
 * components that show an inline <Alert> instead of (or in addition to) a
 * toast. Joins multi-field 422 validation errors onto separate lines.
 */
export function extractErrorMessage(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;

    if (data?.errors) {
      return Object.entries(data.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
    }

    return data?.message ?? null;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return null;
}
